import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import { useSeo } from '@/hooks/useSeo';
import { news } from '@/mocks/news';

export default function NewsDetail() {
  const { id } = useParams();
  const item = news.find((n) => n.id === id);

  useSeo({
    title: item ? item.title : '资讯详情 - 夏目彩春',
    description: item ? item.summary : '夏目彩春影视资讯详情。',
    keywords: item ? `夏目彩春,${item.category},${item.title}` : '夏目彩春资讯',
  });

  if (!item) {
    return (
      <div className="w-full max-w-content mx-auto px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-file-unknow-line"
          title="未找到该资讯"
          description="你访问的资讯可能已被移除或链接有误。"
          actionLabel="返回资讯列表"
          actionTo="/news"
        />
      </div>
    );
  }

  const related = news
    .filter((n) => n.id !== item.id)
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .filter((n) => n.category === item.category)
    .concat(news.filter((n) => n.id !== item.id && n.category !== item.category))
    .slice(0, 4);

  return (
    <div className="bg-background-50">
      <div className="w-full max-w-content mx-auto px-4 md:px-8 py-8 md:py-12">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '资讯', to: '/news' }, { label: item.title }]} />

        <article className="mt-6 max-w-3xl mx-auto">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="px-3 py-1 rounded-md bg-accent-100 text-accent-700 text-xs">{item.category}</span>
          </div>
          <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950 leading-snug">
            {item.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200/80">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              <span className="font-label">{item.date}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {item.author}
            </span>
          </div>

          <p className="mt-6 text-base md:text-lg text-foreground-800 leading-relaxed font-medium">{item.summary}</p>

          <div className="mt-7 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
            <img
              src={item.cover}
              alt={`${item.title} 配图`}
              title={`${item.title} - 夏目彩春影视资讯`}
              className="w-full h-full object-top object-cover"
            />
          </div>

          <div className="mt-8 flex flex-col gap-5">
            {item.content.map((block, i) =>
              block.type === 'heading' ? (
                <h2 key={i} className="font-heading font-bold text-xl text-foreground-950 mt-3 flex items-center gap-2.5">
                  <span className="w-1.5 h-5 rounded-full bg-accent-500" />
                  {block.text}
                </h2>
              ) : (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                  {block.text}
                </p>
              ),
            )}
          </div>

          <div className="mt-10 pt-6 border-t border-background-200/80">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
          </div>
        </article>

        {related.length > 0 && (
          <div className="mt-14 md:mt-16">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5 flex items-center gap-2.5">
              <span className="w-1.5 h-6 rounded-full bg-accent-500" />
              相关资讯推荐
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {related.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  className="group flex gap-4 bg-background-100 border border-background-200/80 rounded-lg p-4 hover:border-primary-300 transition-colors"
                >
                  <div className="w-24 h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img
                      src={n.cover}
                      alt={`${n.title} 配图`}
                      title={`${n.title} - 夏目彩春影视资讯`}
                      className="w-full h-full object-top object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-xs text-foreground-400 font-label">{n.date}</span>
                    <h3 className="mt-1 text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {n.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}